(function () {
  "use strict";

  if (typeof window === "undefined") return;
  if (window.__auCartQueueLoaded) return;
  window.__auCartQueueLoaded = true;

  var WRITE_RE = /\/cart\/(add|update|change|clear)(\.js|\.json)?(\?|$)/;
  var ADD_RE = /\/cart\/add(\.js|\.json)?(\?|$)/;
  var RETRYABLE_RE = /\/cart\/(update|change|clear)(\.js|\.json)?(\?|$)/;
  var READ_RE = /\/cart(\.js|\.json)(\?|$)/;
  var UPDATE_RE = /\/cart\/update(\.js|\.json)?(\?|$)/;
  var STALE_LINE = /updates parameter is invalid/i;
  var CONFLICT = 409;
  var MAX_RETRIES = 2;
  var RETRY_BASE_MS = 120;
  var NET_RETRIES = 2;
  var NET_BASE_MS = 180;
  var WRITE_TIMEOUT_MS = 15000;

  var origFetch = window.fetch;
  if (typeof origFetch !== "function") return;

  var chain = Promise.resolve();
  var stats = {
    queued: 0,
    conflicts: 0,
    retried: 0,
    recovered: 0,
    exhausted: 0,
    netFailed: 0,
    netRetried: 0,
    netRecovered: 0,
    netExhausted: 0,
    timedOut: 0,
    staleLines: 0,
    addsPending: 0,
    writesPending: 0,
  };
  window.__auCartQueueStats = stats;

  function urlOf(input) {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.href;
    return (input && input.url) || "";
  }

  function withDeadline(args) {
    if (typeof AbortController !== "function") return { args: args, done: noop };
    var ctrl = new AbortController();
    var input = args[0];
    var init = args[1] || {};
    var caller = init.signal || (input && typeof input === "object" && input.signal);
    if (caller && caller.aborted) ctrl.abort(caller.reason);
    else if (caller && caller.addEventListener) {
      caller.addEventListener("abort", function () {
        ctrl.abort(caller.reason);
      }, { once: true });
    }
    var timer = setTimeout(function () {
      stats.timedOut++;
      ctrl.abort(typeof DOMException === "function" ? new DOMException("signal timed out", "TimeoutError") : undefined);
    }, WRITE_TIMEOUT_MS);
    var copy = Array.prototype.slice.call(args);
    copy[1] = Object.assign({}, init, { signal: ctrl.signal });
    return {
      args: copy,
      done: function () {
        clearTimeout(timer);
      },
    };
  }

  function flagStale(response) {
    response
      .clone()
      .json()
      .then(function (err) {
        if (!err || !STALE_LINE.test(String(err.description || err.message || ""))) return;
        stats.staleLines++;
        document.dispatchEvent(new CustomEvent("au:cart:stale"));
      })
      .catch(noop);
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function announce(event, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign({ event: event, source: "au_cart_queue" }, detail || {})
      );
    } catch (e) {}
  }

  function send(self, args, retryable, attempt) {
    return origFetch.apply(self, args).then(
      function (response) {
        if (response.status !== CONFLICT) return response;
        stats.conflicts++;
        if (!retryable || attempt >= MAX_RETRIES) {
          stats.exhausted++;
          return response;
        }
        stats.retried++;
        return delay(RETRY_BASE_MS * (attempt + 1)).then(function () {
          return send(self, args, retryable, attempt + 1).then(function (retried) {
            if (retried.status !== CONFLICT) stats.recovered++;
            return retried;
          });
        });
      },
      function (err) {
        if (err && (err.name === "TimeoutError" || err.name === "AbortError")) throw err;
        stats.netFailed++;
        if (!retryable || attempt >= NET_RETRIES) {
          stats.netExhausted++;
          throw err;
        }
        stats.netRetried++;
        return delay(NET_BASE_MS * Math.pow(2, attempt) + Math.random() * 120).then(
          function () {
            return send(self, args, retryable, attempt + 1);
          }
        );
      }
    );
  }

  function sendWithRecovery(self, args, retryable, label) {
    var before = stats.netRetried;
    return send(self, args, retryable, 0).then(function (response) {
      if (stats.netRetried > before) {
        stats.netRecovered++;
        announce("au_cart_recovered", { au_cart_request: label });
      }
      return response;
    });
  }

  window.fetch = function (input) {
    var self = this || window;
    var url;
    try {
      url = urlOf(input);
    } catch (e) {
      return origFetch.apply(self, arguments);
    }
    var isWrite = WRITE_RE.test(url);
    var isRead = !isWrite && READ_RE.test(url);
    if (!url || (!isWrite && !isRead)) return origFetch.apply(self, arguments);

    var args = arguments;
    var bodyReusable = typeof input === "string" || input instanceof URL;

    if (isRead) {
      var fresh = Array.prototype.slice.call(args);
      fresh[1] = Object.assign({}, fresh[1], { cache: "no-store" });
      return sendWithRecovery(self, fresh, true, "read");
    }

    var retryable = bodyReusable && RETRYABLE_RE.test(url);

    stats.queued++;
    stats.writesPending++;
    var run = function () {
      var deadline = withDeadline(args);
      return sendWithRecovery(self, deadline.args, retryable, "write")
        .then(function (response) {
          if (response.status === 422 && UPDATE_RE.test(url)) flagStale(response);
          return response;
        })
        .then(
          function (response) {
            deadline.done();
            return response;
          },
          function (err) {
            deadline.done();
            throw err;
          }
        );
    };
    var result = chain.then(run, run);

    chain = result.then(noop, noop);

    var targets = writeTargets(url, args);
    result.then(
      function (response) {
        settleWrite(!!(response && response.ok), targets);
      },
      function () {
        settleWrite(false, targets);
      }
    );

    if (ADD_RE.test(url)) {
      stats.addsPending++;
      var addedId = addedVariantId(args);
      var startedAt = typeof performance !== "undefined" ? performance.now() : 0;
      result.then(
        function (response) {
          emitAddSettled(response && response.ok, response && response.status, addedId, startedAt);
          if (response && !response.ok) emitAddError(response, addedId);
        },
        function () {
          emitAddSettled(false, 0, addedId, startedAt);
        }
      );
    }

    return result;
  };

  function addedVariantId(args) {
    try {
      var init = args && args[1];
      var body = init && init.body;
      if (!body) return null;
      if (typeof body === "string") {
        if (body.charAt(0) === "{") {
          var j = JSON.parse(body);
          if (j && j.id != null) return String(j.id);
          if (j && j.items && j.items[0] && j.items[0].id != null) return String(j.items[0].id);
          return null;
        }
        return new URLSearchParams(body).get("id");
      }
      if (typeof FormData !== "undefined" && body instanceof FormData) {
        var v = body.get("id");
        return v == null ? null : String(v);
      }
      if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        return body.get("id");
      }
    } catch (e) {}
    return null;
  }

  function bodyFields(args) {
    var body = args && args[1] && args[1].body;
    if (!body) return null;
    if (typeof body === "string" && body.charAt(0) === "{") return JSON.parse(body);
    var params = typeof body === "string" ? new URLSearchParams(body) : body;
    if (typeof params.forEach !== "function") return null;
    var out = {};
    var lines = 0;
    params.forEach(function (value, key) {
      var m = /^updates\[(.*)\]$/.exec(key);
      if (m) {
        out.updates = out.updates || {};
        out.updates[m[1] === "" ? "l:" + ++lines : m[1]] = value;
      } else if (key === "id" || key === "line") {
        out[key] = value;
      }
    });
    return out;
  }

  function writeTargets(url, args) {
    if (/\/cart\/clear/.test(url)) return ["*"];
    var data;
    try {
      data = bodyFields(args);
    } catch (e) {
      return [];
    }
    if (!data) return [];
    if (ADD_RE.test(url)) {
      return (Array.isArray(data.items) ? data.items : [data])
        .filter(function (item) {
          return item && item.id != null;
        })
        .map(function (item) {
          return "v:" + item.id;
        });
    }
    if (data.line != null) return ["l:" + data.line];
    if (data.id != null) return ["k:" + data.id];
    var updates = data.updates;
    if (Array.isArray(updates)) {
      return updates.map(function (_, i) {
        return "l:" + (i + 1);
      });
    }
    return updates
      ? Object.keys(updates).map(function (key) {
          return key.indexOf("l:") === 0 ? key : "k:" + key;
        })
      : [];
  }

  function settleWrite(ok, targets) {
    stats.writesPending--;
    try {
      document.dispatchEvent(new CustomEvent("au:cart:write-settled", { detail: { ok: ok, targets: targets } }));
      if (!stats.writesPending) document.dispatchEvent(new CustomEvent("au:cart:idle"));
    } catch (e) {}
  }

  function emitAddSettled(ok, status, id, startedAt) {
    stats.addsPending--;
    try {
      document.dispatchEvent(
        new CustomEvent("au:cart:add-settled", {
          detail: { ok: !!ok, status: status || 0, id: id || null, startedAt: startedAt || 0 },
        })
      );
    } catch (e) {}
  }

  function emitAddError(response, id) {
    var clone;
    try {
      clone = response.clone();
    } catch (e) {
      return;
    }
    clone
      .json()
      .then(function (data) {
        var text = data && (data.description || data.message);
        if (!text) return;
        document.dispatchEvent(
          new CustomEvent("au:cart:add-error", {
            detail: {
              ok: false,
              status: response.status || 0,
              id: id || null,
              description: String(text),
            },
          })
        );
      })
      .catch(function () {});
  }

  function noop() {}
})();
